import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzTableCellDirective, NzTableComponent, NzTableModule, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { Subject, takeUntil } from 'rxjs';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { PackagingTypes as PackagingTypesService } from 'src/app/private/services/packaging-types.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { PackagingTypeDto } from 'src/app/shared/dto/packaging-type.dto';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';

@Component({
    selector: 'app-modal-select-article-packaging',
    standalone: true,
    imports: [
        NzTableModule,
        NzModalFooterDirective,
        NzTagModule,
        NzIconModule,
        NzCheckboxComponent,
        FormsModule,
        CommonModule,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzModalTitleDirective,
        NzTableCellDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        ReactiveFormsModule,
        NzMessageModule,
        NzInputDirective,
        NzEmptyModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './modal-select-article-packaging.component.html',
    styleUrl: './modal-select-article-packaging.component.scss'
})
export class ModalSelectArticlePackagingComponent implements OnInit, AfterViewInit, OnDestroy {
    @Output() emitService = new EventEmitter();
    @ViewChild('modalContent') private readonly _modalContent!: ElementRef;

    isLoading: boolean = true;
    isSaving: boolean = false;

    packagingTypes: PackagingTypeDto[] = [];
    selectedPackagingIds: number[] = [];
    canSetProducedQuantity: boolean = false;
    justWatching: boolean = false;
    remainingQuantityToProduce: number = 0;
    tableHeight!: number;

    private selectedPackagings: PackagingDto[];
    private packagingTypeService = inject(PackagingTypesService);
    private readonly toast = inject(NzMessageService);

    private unsubscribe$ = new Subject<void>();

    constructor(@Inject(NZ_MODAL_DATA) public data: any) {
        this.canSetProducedQuantity = data?.canSetProducedQuantity;
        this.justWatching = data?.justWatching;
        this.remainingQuantityToProduce = data?.remainingQuantityToProduce;
        this.selectedPackagings = data?.selectedPackagings;
    }

    async ngOnInit() {
        await this.packagingTypeService.setupWebSocket();
        this.packagingTypeService.packagingTypes$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (packagingTypes) => {
                    this.packagingTypes = packagingTypes;
                },
                error: () => {
                    this.toast.error('Error cargado tipos de paquetes');
                }
            });

        if(this.selectedPackagings !== undefined){
            this.loadPackagingsData();
        }

        this.isLoading = false;
    }

    async ngAfterViewInit(): Promise<void> {
        setTimeout(() => {
            this.tableHeight =
                (this._modalContent.nativeElement as HTMLImageElement)
                    .clientHeight-TITLE_TABLE_SIZE;
        });
    }

    ngOnDestroy(): void {
        this.packagingTypeService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    setAsSelected(packagingTypeId: number) {
        if (this.selectedPackagingIds.includes(packagingTypeId)) {
            this.selectedPackagingIds.splice(this.selectedPackagingIds.indexOf(packagingTypeId), 1);

            return;
        }

        this.selectedPackagingIds.push(packagingTypeId);
    }

    save() {
        this.isSaving = true;
        const packagingsToAssign: PackagingTypeDto[] = [];

        for (const selectedPackagingId of this.selectedPackagingIds) {
            const foundPackagingType: PackagingTypeDto = this.packagingTypes.find(p => p.id === selectedPackagingId)!;

            packagingsToAssign.push(foundPackagingType);
        }

        if (!this.validate(packagingsToAssign)) {
            this.isSaving = false;
            return;
        }

        this.isSaving = false;
        this.emitService.next(packagingsToAssign);
    }

    validate(packagingsToAssing: PackagingTypeDto[]): boolean {
        if (packagingsToAssing.length === 0) {
            this.toast.error('Debe seleccionar al menos un tipo de paquete');
            return false;
        }

        if (packagingsToAssing.some(packaging => packaging.quantity == null || packaging.quantity <= 0)) {
            this.toast.error('Has de indicar una cantidad para todos los paquetes seleccionados.');
            return false;
        }

        const totalCapacity = packagingsToAssing.reduce((sum, pkg) => sum + pkg.quantityPerPackaging * pkg.quantity!, 0);

        if (totalCapacity < this.remainingQuantityToProduce) {
            this.toast.error('La suma de las cantidades de los paquetes seleccionados es menor a la cantidad a producir');
            return false;
        }

        let remainingUnits = this.remainingQuantityToProduce;
        let counter: number = 0;

        for (const { quantityPerPackaging, quantity } of packagingsToAssing) {
            const maxForThisPackaging = quantityPerPackaging * quantity!;
            const usedCapacity = Math.min(remainingUnits, maxForThisPackaging);
            counter++;

            remainingUnits -= usedCapacity;

            if (usedCapacity < maxForThisPackaging && quantity! > Math.ceil(usedCapacity / quantityPerPackaging)) {
                this.toast.error(`Has indicado demasiados paquetes y se quedarían algunos vacíos.`);
                return false;
            }

            if (remainingUnits === 0 && counter === packagingsToAssing.length) {
                return true;
            }
        }

        if (remainingUnits !== 0) {
            this.toast.error(`Quedan unidades pendientes de repartir (${remainingUnits})`);
            return false;
        }

        return true;
    }

    handleCancel() {
        this.emitService.next(undefined);
    }

    roundUp(value: number): number {
        return Math.ceil(value);
    }

    private loadPackagingsData() {
        try {
            const seenPackagingTypes = new Set<number>();

            const uniquePackagingTypeIds = this.selectedPackagings.filter((packaging) => {
                if (seenPackagingTypes.has(packaging.packagingTypeId)) {
                    return false;
                }

                seenPackagingTypes.add(packaging.packagingType!.id!);
                return true;
            }).map(packaging => packaging.packagingType!.id!);

            for (const typeId of uniquePackagingTypeIds) {
                if (!this.selectedPackagingIds.includes(typeId)) {
                    this.selectedPackagingIds.push(typeId);
                }
            }

            this.countPackagingTypes(this.selectedPackagings).forEach((count, packagingTypeId) => {
                const indexPackagingType = this.packagingTypes.findIndex(packagingType => packagingType.id === packagingTypeId);
                this.packagingTypes[indexPackagingType].quantity = count;
            });
        } catch (error) {
            this.toast.error('Error cargando paquetes previos');
        }

    }

    private countPackagingTypes(packagings: PackagingDto[]): Map<number, number> {
        const packagingTypeCounts = new Map<number, number>();

        packagings.forEach(packaging => {
            const packagingTypeId = packaging.packagingType!.id!;
            if (packagingTypeCounts.has(packagingTypeId)) {
                packagingTypeCounts.set(packagingTypeId, packagingTypeCounts.get(packagingTypeId)! + 1);
            } else {
                packagingTypeCounts.set(packagingTypeId, 1);
            }
        });

        return packagingTypeCounts;
    }
}

